import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import useEscapeKey from '../hooks/useEscapeKey';
import { formatCurrency, calculatePensionContribution, employeeContributionBreakdown, monthlyGrossForPension } from '../utils/pensionCalc';

const SCHEME_OPTIONS = [
  { value: '', label: '— not set —' },
  { value: 'salary_sacrifice', label: 'Salary Sacrifice (pre-tax)' },
  { value: 'net_pay', label: 'Net Pay (pre-tax)' },
  { value: 'relief_at_source', label: 'Relief at Source (post-tax)' },
  { value: 'none', label: 'No pension scheme — not required' },
];

const STATUS_OPTIONS = [
  { value: '', label: '— not set —', description: 'Pension status not configured' },
  { value: 'enrolled', label: 'Enrolled', description: 'Contributing each pay period' },
  { value: 'opted-out', label: 'Opted Out', description: 'Eligible but has opted out' },
  { value: 'deferred', label: 'Deferred', description: 'Auto-enrolment postponed' },
  { value: 'not-eligible', label: 'Not Eligible', description: 'Below earnings threshold / under 22 / over SPA' },
  { value: 'exempt', label: 'Exempt', description: 'Outside auto-enrolment (e.g. director who is not a worker)' },
];

const STATUS_COLOURS = {
  '': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  'enrolled': 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  'opted-out': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  'deferred': 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  'not-eligible': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
  'exempt': 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300',
};

// Per-row state shape lives separately to the API rows so it can be edited
// without losing pending changes if the upstream data refreshes.
function rowToState(row) {
  return {
    employee_id: row.employee_id,
    first_name: row.first_name,
    last_name: row.last_name,
    annual_salary: parseFloat(row.annual_salary) || 0,
    // Pro-rata / pay-type fields carried through so the contribution preview
    // can use the employee's actual (pro-rata-aware) monthly pay, not the FTE.
    prorata_salary: row.prorata_salary,
    contract_type: row.contract_type,
    pay_type: row.pay_type,
    hourly_rate: row.hourly_rate,
    hours_this_period: row.hours_this_period,
    commission: row.commission,
    // dirty flag so we only send rows the user touched
    dirty: false,
    // Editable fields
    status: row.pension_status || '',
    earnings_basis: row.earnings_basis || 'qualifying',
    employer_type: row.employer_type || 'percentage',
    employer_value: row.employer_value != null ? String(row.employer_value) : '3',
    employee_type: row.employee_type || 'percentage',
    employee_value: row.employee_value != null ? String(row.employee_value) : '5',
    // Empty string means "inherit company default"
    scheme_type: row.scheme_type || '',
  };
}

export default function PensionMassSetupModal({ companyId, onClose, onSaved }) {
  useEscapeKey(onClose);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [defaultSchemeType, setDefaultSchemeType] = useState('');
  const [defaultSchemeTypeDirty, setDefaultSchemeTypeDirty] = useState(false);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('all'); // all | not_set | enrolled
  const [bulkStatus, setBulkStatus] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    api.get(`/pensions/company/${companyId}/scheme-default`)
      .then(res => {
        if (cancelled) return;
        setCompanyName(res.data.company_name || '');
        setDefaultSchemeType(res.data.default_pension_scheme_type || '');
        setRows((res.data.all_employees || []).map(rowToState));
      })
      .catch(err => !cancelled && setError(err.response?.data?.error || err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [companyId]);

  const updateRow = (id, patch) => {
    setRows(prev => prev.map(r => r.employee_id === id ? { ...r, ...patch, dirty: true } : r));
  };

  const filteredRows = useMemo(() => {
    if (filter === 'not_set') return rows.filter(r => !r.status);
    if (filter === 'enrolled') return rows.filter(r => r.status === 'enrolled');
    return rows;
  }, [rows, filter]);

  const counts = useMemo(() => ({
    total: rows.length,
    not_set: rows.filter(r => !r.status).length,
    enrolled: rows.filter(r => r.status === 'enrolled').length,
    opted_out: rows.filter(r => r.status === 'opted-out').length,
    deferred: rows.filter(r => r.status === 'deferred').length,
    not_eligible: rows.filter(r => r.status === 'not-eligible').length,
  }), [rows]);

  const dirtyCount = rows.filter(r => r.dirty).length;

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectVisible = () => {
    const visibleIds = filteredRows.map(r => r.employee_id);
    const allSelected = visibleIds.every(id => selectedIds.has(id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allSelected) for (const id of visibleIds) next.delete(id);
      else for (const id of visibleIds) next.add(id);
      return next;
    });
  };

  const applyBulkStatus = () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setRows(prev => prev.map(r =>
      selectedIds.has(r.employee_id) ? { ...r, status: bulkStatus, dirty: true } : r
    ));
    setSelectedIds(new Set());
    setBulkStatus('');
  };

  const handleSave = async () => {
    const toSend = rows.filter(r => r.dirty && r.status).map(r => ({
      employee_id: r.employee_id,
      status: r.status,
      earnings_basis: r.earnings_basis,
      employer_type: r.employer_type,
      employer_value: parseFloat(r.employer_value) || 0,
      employee_type: r.employee_type,
      employee_value: parseFloat(r.employee_value) || 0,
      scheme_type: r.scheme_type || null,
    }));
    if (toSend.length === 0 && !defaultSchemeTypeDirty) {
      setError('Nothing changed — set a status on at least one employee or pick a default scheme type.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await api.post(`/pensions/company/${companyId}/mass-setup`, {
        default_scheme_type: defaultSchemeTypeDirty ? (defaultSchemeType || null) : undefined,
        apply_default_to_enrolled: defaultSchemeTypeDirty && !!defaultSchemeType,
        employees: toSend,
      });
      if (onSaved) onSaved(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Monthly contribution preview per row (helps the user sanity-check the %).
  const monthlyFor = (r) => {
    if (r.status !== 'enrolled') return null;
    const monthly = monthlyGrossForPension(r);
    const eeGross = calculatePensionContribution(r.employee_type, r.employee_value, monthly, r.earnings_basis);
    const er = calculatePensionContribution(r.employer_type, r.employer_value, monthly, r.earnings_basis);
    // Effective scheme = per-employee override, else the company default
    // (which is what an unset row inherits on save). 'none' isn't a real scheme.
    const effectiveScheme = r.scheme_type || (defaultSchemeType !== 'none' ? defaultSchemeType : '');
    const ee = employeeContributionBreakdown(eeGross, effectiveScheme);
    return { ee, er };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="p-6 pb-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Pension Setup{companyName ? ` — ${companyName}` : ''}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Set each employee's pension status. Use the bulk control to stamp the same status onto multiple at once.
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Loading…</div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">
                  {error}
                </div>
              )}

              {/* Company default scheme type */}
              <div className="mb-5 p-4 rounded-lg bg-brand-50 dark:bg-brand-900/20 border border-brand-200 dark:border-brand-800">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 dark:text-gray-100">
                      Company default scheme type
                    </label>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                      Inherited by new enrolments and any employee row left blank below.
                    </p>
                  </div>
                  <select
                    value={defaultSchemeType}
                    onChange={(e) => { setDefaultSchemeType(e.target.value); setDefaultSchemeTypeDirty(true); }}
                    className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm py-1.5"
                  >
                    {SCHEME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {defaultSchemeType === 'none' && (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                    Confirms this company doesn't operate a pension scheme. Saving will mark every not-set employee as <span className="font-medium">Not Eligible</span> and silence the setup banner. If anyone is currently <span className="font-medium">Enrolled</span> below, the banner will flag the inconsistency for you to reconcile.
                  </p>
                )}
              </div>

              {/* Counts + filter + bulk action */}
              <div className="mb-3 flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3 flex-wrap text-xs">
                  <span className="text-gray-500 dark:text-gray-400">
                    {counts.total} employee{counts.total === 1 ? '' : 's'} ·
                    <span className="text-gray-700 dark:text-gray-300 ml-1">{counts.enrolled} enrolled</span>
                    {counts.not_set > 0 && <span className="text-amber-600 dark:text-amber-400 ml-1">· {counts.not_set} not set</span>}
                    {counts.opted_out > 0 && <span className="text-yellow-600 dark:text-yellow-400 ml-1">· {counts.opted_out} opted out</span>}
                    {counts.deferred > 0 && <span className="text-blue-600 dark:text-blue-400 ml-1">· {counts.deferred} deferred</span>}
                    {counts.not_eligible > 0 && <span className="text-gray-500 ml-1">· {counts.not_eligible} not eligible</span>}
                  </span>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs py-1"
                  >
                    <option value="all">Show all</option>
                    <option value="not_set">Only not set</option>
                    <option value="enrolled">Only enrolled</option>
                  </select>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-gray-500 dark:text-gray-400">
                    {selectedIds.size} selected — bulk set to:
                  </span>
                  <select
                    value={bulkStatus}
                    onChange={(e) => setBulkStatus(e.target.value)}
                    className="rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs py-1"
                  >
                    <option value="">— pick status —</option>
                    {STATUS_OPTIONS.filter(o => o.value).map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={applyBulkStatus}
                    disabled={!bulkStatus || selectedIds.size === 0}
                    className="px-3 py-1 rounded text-xs bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-40"
                  >
                    Apply
                  </button>
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                    <tr className="text-xs uppercase text-gray-500 dark:text-gray-400">
                      <th className="px-2 py-2 w-8 text-center">
                        <input
                          type="checkbox"
                          checked={filteredRows.length > 0 && filteredRows.every(r => selectedIds.has(r.employee_id))}
                          onChange={toggleSelectVisible}
                          className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                          aria-label="Select visible"
                        />
                      </th>
                      <th className="px-3 py-2 text-left">Employee</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-2 py-2 text-right">EE %</th>
                      <th className="px-2 py-2 text-right">ER %</th>
                      <th className="px-2 py-2 text-left">Earnings basis</th>
                      <th className="px-2 py-2 text-left">Scheme</th>
                      <th className="px-3 py-2 text-right">Monthly EE / ER</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {filteredRows.length === 0 && (
                      <tr>
                        <td colSpan="8" className="px-4 py-6 text-center text-sm text-gray-400">
                          No employees match this filter.
                        </td>
                      </tr>
                    )}
                    {filteredRows.map(r => {
                      const monthly = monthlyFor(r);
                      const isEnrolled = r.status === 'enrolled';
                      const isSelected = selectedIds.has(r.employee_id);
                      return (
                        <tr key={r.employee_id} className={`${r.dirty ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''} hover:bg-gray-50 dark:hover:bg-gray-700/30`}>
                          <td className="px-2 py-2 text-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelected(r.employee_id)}
                              className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-gray-900 dark:text-gray-100">{r.first_name} {r.last_name}</div>
                            <div className="text-[10px] text-gray-400">{formatCurrency(r.annual_salary)}/yr</div>
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={r.status}
                              onChange={(e) => updateRow(r.employee_id, { status: e.target.value })}
                              className={`rounded text-xs py-1 px-2 border-0 ${STATUS_COLOURS[r.status] || STATUS_COLOURS['']}`}
                            >
                              {STATUS_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-2 text-right">
                            <input
                              type="number" step="0.01" min="0" max="100"
                              disabled={!isEnrolled}
                              value={isEnrolled ? r.employee_value : ''}
                              onChange={(e) => updateRow(r.employee_id, { employee_value: e.target.value })}
                              placeholder={isEnrolled ? '0' : '—'}
                              className="w-16 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs py-1 px-2 text-right disabled:opacity-30 disabled:bg-gray-50"
                            />
                          </td>
                          <td className="px-2 py-2 text-right">
                            <input
                              type="number" step="0.01" min="0" max="100"
                              disabled={!isEnrolled}
                              value={isEnrolled ? r.employer_value : ''}
                              onChange={(e) => updateRow(r.employee_id, { employer_value: e.target.value })}
                              placeholder={isEnrolled ? '0' : '—'}
                              className="w-16 rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs py-1 px-2 text-right disabled:opacity-30 disabled:bg-gray-50"
                            />
                          </td>
                          <td className="px-2 py-2">
                            <select
                              disabled={!isEnrolled}
                              value={r.earnings_basis}
                              onChange={(e) => updateRow(r.employee_id, { earnings_basis: e.target.value })}
                              className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs py-1 disabled:opacity-30"
                            >
                              <option value="qualifying">Qualifying</option>
                              <option value="full">Full</option>
                            </select>
                          </td>
                          <td className="px-2 py-2">
                            <select
                              disabled={!isEnrolled}
                              value={r.scheme_type}
                              onChange={(e) => updateRow(r.employee_id, { scheme_type: e.target.value })}
                              className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-xs py-1 disabled:opacity-30"
                              title={
                                r.scheme_type
                                  ? ''
                                  : (defaultSchemeType === 'none'
                                      ? 'Company default is "no scheme" — set a scheme type per-employee or change the company default'
                                      : `Inherits company default${defaultSchemeType ? ` (${SCHEME_OPTIONS.find(o => o.value === defaultSchemeType)?.label})` : ''}`)
                              }
                            >
                              <option value="">
                                {defaultSchemeType && defaultSchemeType !== 'none'
                                  ? '↳ default'
                                  : '— not set —'}
                              </option>
                              <option value="salary_sacrifice">Salary sacrifice</option>
                              <option value="net_pay">Net pay</option>
                              <option value="relief_at_source">Relief at source</option>
                            </select>
                          </td>
                          <td className="px-3 py-2 text-right text-xs">
                            {monthly ? (
                              <div>
                                <span className="text-gray-700 dark:text-gray-300">
                                  {formatCurrency(monthly.ee.fromPay)} / {formatCurrency(monthly.er)}
                                </span>
                                {monthly.ee.reliefAtSource && (
                                  <div className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap"
                                    title="Relief at Source: employee pays 80% from net pay; HMRC tops up the rest into the pot">
                                    +{formatCurrency(monthly.ee.taxRelief)} relief · {formatCurrency(monthly.ee.pot)} pot
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0 bg-gray-50 dark:bg-gray-800">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {dirtyCount > 0 && `${dirtyCount} employee${dirtyCount === 1 ? '' : 's'} changed`}
            {dirtyCount > 0 && defaultSchemeTypeDirty && ' · '}
            {defaultSchemeTypeDirty && 'company default changed'}
            {dirtyCount === 0 && !defaultSchemeTypeDirty && 'No changes yet'}
          </span>
          <div className="flex gap-3">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
              Cancel
            </button>
            <button type="button" onClick={handleSave}
              disabled={saving || (dirtyCount === 0 && !defaultSchemeTypeDirty)}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-md hover:bg-brand-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save all'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
